import { useEffect, useRef } from 'react';
import { getSharedAudioContext, resumeAudioContext } from '@/lib/audio';

// ─── "Clarity" - Steve Jobs-inspired elefa Connect Ringtone ─────────────────
// Design philosophy: Minimalist. Unmistakable. Feels like it was always there.
// A crystalline marimba-like tone - warm sine body with bright harmonic shimmer.
// Melody: C6 → E6 → G6 → C7 (pure C major - universal, calming, iconic)
// Two-bar phrase with breathing space. No anxiety. Just presence.

const MELODY: { freq: number; dur: number; vel: number }[] = [
  // Bar 1: ascending invitation
  { freq: 1046.50, dur: 140, vel: 0.8 },  // C6
  { freq: 1318.51, dur: 140, vel: 0.7 },  // E6
  { freq: 1567.98, dur: 160, vel: 0.9 },  // G6 - slight emphasis
  { freq: 2093.00, dur: 260, vel: 1.0 },  // C7 - resolve, longest
  // Bar 2: gentle echo (softer repeat of first two)
  { freq: 1046.50, dur: 120, vel: 0.5 },  // C6 echo
  { freq: 1318.51, dur: 200, vel: 0.45 }, // E6 fading
];

const NOTE_GAP_MS = 50;      // Tight but not rushed
const CYCLE_PAUSE_MS = 1600; // Generous breath between rings
const ATTACK_MS = 8;         // Crisp, instant attack - like tapping glass
const DECAY_MS = 40;         // Quick initial decay for percussive feel
const RELEASE_MS = 350;      // Long, airy release - the "Jobs tail"

// ─── Vibration pattern (Signal-style haptic rhythm) ─────────────────────────
// Mirrors the melodic rhythm: short pulses for notes, longer for resolve
const VIBRATION_PATTERN = [
  0,    // start immediately
  80,   // C6 buzz
  70,   // gap
  80,   // E6 buzz
  70,   // gap
  100,  // G6 buzz (slightly longer)
  60,   // gap
  200,  // C7 buzz (resolve - satisfying long pulse)
  400,  // rest
  60,   // C6 echo
  80,   // gap
  120,  // E6 fade
  1600, // cycle pause
];

interface UseElefaRingtoneOptions {
  isPlaying: boolean;
  volume?: number; // 0–1, default 0.35
}

// ─── Offline WAV renderer ───────────────────────────────────────────────────

function renderMelodyToBuffer(sampleRate: number): AudioBuffer {
  let totalMs = 0;
  MELODY.forEach((note) => { totalMs += note.dur + NOTE_GAP_MS; });
  totalMs -= NOTE_GAP_MS;
  totalMs += RELEASE_MS + CYCLE_PAUSE_MS;

  const totalDurationMs = totalMs * 2; // 2 cycles
  const totalSamples = Math.ceil((totalDurationMs / 1000) * sampleRate);

  const buffer = new AudioBuffer({
    numberOfChannels: 1,
    length: totalSamples,
    sampleRate,
  });
  const channel = buffer.getChannelData(0);

  for (let cycle = 0; cycle < 2; cycle++) {
    const cycleOffsetSamples = Math.floor((cycle * totalMs / 1000) * sampleRate);
    let noteOffsetMs = 0;

    MELODY.forEach((note) => {
      const noteStartSample = cycleOffsetSamples + Math.floor((noteOffsetMs / 1000) * sampleRate);
      const totalNoteDur = note.dur + RELEASE_MS;
      const noteDurationSamples = Math.floor((totalNoteDur / 1000) * sampleRate);
      const attackSamples = Math.floor((ATTACK_MS / 1000) * sampleRate);
      const decaySamples = Math.floor((DECAY_MS / 1000) * sampleRate);
      const sustainSamples = Math.floor((note.dur / 1000) * sampleRate);

      for (let i = 0; i < noteDurationSamples; i++) {
        const sampleIndex = noteStartSample + i;
        if (sampleIndex >= totalSamples) break;

        const t = i / sampleRate;

        // Pure sine body - the warmth
        const fundamental = Math.sin(2 * Math.PI * note.freq * t);
        // Bright harmonic at 3x - the crystalline shimmer
        const harmonic3 = Math.sin(2 * Math.PI * note.freq * 3 * t) * 0.12;
        // Subtle 5th harmonic - adds "glass bell" character
        const harmonic5 = Math.sin(2 * Math.PI * note.freq * 5 * t) * 0.04;
        // Very gentle detuned chorus for width
        const chorus = Math.sin(2 * Math.PI * note.freq * 1.001 * t) * 0.15;

        // ADSR envelope - percussive marimba character
        let envelope = 0;
        if (i < attackSamples) {
          // Instant attack
          envelope = (i / attackSamples);
        } else if (i < attackSamples + decaySamples) {
          // Quick decay to sustain level
          const decayProgress = (i - attackSamples) / decaySamples;
          envelope = 1.0 - decayProgress * 0.35; // decay to 0.65
        } else if (i < sustainSamples) {
          // Gentle sustain decay
          const sustainProgress = (i - attackSamples - decaySamples) / (sustainSamples - attackSamples - decaySamples);
          envelope = 0.65 * (1 - sustainProgress * 0.15);
        } else {
          // Exponential release - the airy tail
          const releaseProgress = (i - sustainSamples) / (noteDurationSamples - sustainSamples);
          envelope = 0.55 * Math.max(0.001, Math.exp(-4 * releaseProgress));
        }

        const sample = (fundamental + chorus + harmonic3 + harmonic5) * envelope * note.vel * 0.28;
        channel[sampleIndex] += sample;
      }

      noteOffsetMs += note.dur + NOTE_GAP_MS;
    });
  }

  return buffer;
}

function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const sampleRate = buffer.sampleRate;
  const bitsPerSample = 16;
  const channelData = buffer.getChannelData(0);
  const dataLength = channelData.length * (bitsPerSample / 8);
  const totalLength = 44 + dataLength;

  const arrayBuffer = new ArrayBuffer(totalLength);
  const view = new DataView(arrayBuffer);

  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  w(0, 'RIFF');
  view.setUint32(4, totalLength - 8, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * (bitsPerSample / 8), true);
  view.setUint16(32, bitsPerSample / 8, true);
  view.setUint16(34, bitsPerSample, true);
  w(36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < channelData.length; i++) {
    const s = Math.max(-1, Math.min(1, channelData[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

// ─── Cached blob URL ────────────────────────────────────────────────────────
let cachedBlobUrl: string | null = null;

function getOrCreateRingtoneBlobUrl(): string {
  if (cachedBlobUrl) return cachedBlobUrl;
  const buffer = renderMelodyToBuffer(44100);
  const blob = audioBufferToWavBlob(buffer);
  cachedBlobUrl = URL.createObjectURL(blob);
  return cachedBlobUrl;
}

// ─── Vibration helper ───────────────────────────────────────────────────────

let vibrationInterval: ReturnType<typeof setInterval> | null = null;

function canUseVibration(): boolean {
  return Boolean(navigator.vibrate && (navigator.userActivation?.hasBeenActive ?? true));
}

function startVibration() {
  if (!canUseVibration()) return;
  // Play pattern once, then repeat on interval matching cycle duration
  const cycleDuration = VIBRATION_PATTERN.reduce((a, b) => a + b, 0);
  try { navigator.vibrate(VIBRATION_PATTERN); } catch { return; }
  vibrationInterval = setInterval(() => {
    try { navigator.vibrate(VIBRATION_PATTERN); } catch { stopVibration(); }
  }, cycleDuration);
}

function stopVibration() {
  if (vibrationInterval) {
    clearInterval(vibrationInterval);
    vibrationInterval = null;
  }
  if (canUseVibration()) {
    try { navigator.vibrate(0); } catch {} // Cancel immediately
  }
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useElefaRingtone({ isPlaying, volume = 0.35 }: UseElefaRingtoneOptions) {
  const masterGainRef = useRef<GainNode | null>(null);
  const cycleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPlayingRef = useRef(false);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (isPlaying) {
      startRingtone();
      startVibration();
    } else {
      stopRingtone();
      stopVibration();
    }

    return () => {
      stopRingtone();
      stopVibration();
    };
  }, [isPlaying, volume]);

  async function startRingtone() {
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;

    const ctx = getSharedAudioContext();

    if (ctx && ctx.state === 'suspended') {
      await resumeAudioContext();
    }

    // Primary: live oscillators
    if (ctx && ctx.state === 'running') {
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(volume, ctx.currentTime);
      masterGain.connect(ctx.destination);
      masterGainRef.current = masterGain;
      playMotif(ctx, masterGain);
      return;
    }

    // Fallback: <audio> element
    console.log('[Ringtone] AudioContext unavailable - using <audio> fallback');
    try {
      const blobUrl = getOrCreateRingtoneBlobUrl();
      const audio = new Audio(blobUrl);
      audio.loop = true;
      audio.volume = Math.min(1, volume * 2);
      audioElementRef.current = audio;

      const playPromise = audio.play();
      if (playPromise) {
        playPromise.catch((err) => {
          console.warn('[Ringtone] Autoplay blocked:', err.message);
          const resume = () => {
            audio.play().catch(() => {});
            document.removeEventListener('click', resume);
            document.removeEventListener('touchstart', resume);
          };
          document.addEventListener('click', resume, { once: true });
          document.addEventListener('touchstart', resume, { once: true });
        });
      }
    } catch (err) {
      console.warn('[Ringtone] Fallback failed:', err);
    }
  }

  function playMotif(ctx: AudioContext, masterGain: GainNode) {
    if (!isPlayingRef.current || ctx.state === 'closed') return;

    const now = ctx.currentTime;
    let offset = 0;

    MELODY.forEach((note) => {
      const noteStart = now + offset / 1000;
      playNote(ctx, masterGain, note.freq, note.dur, noteStart, note.vel);
      offset += note.dur + NOTE_GAP_MS;
    });

    const totalCycleMs = offset - NOTE_GAP_MS + CYCLE_PAUSE_MS;

    cycleTimerRef.current = setTimeout(() => {
      playMotif(ctx, masterGain);
    }, totalCycleMs);
  }

  function playNote(
    ctx: AudioContext,
    destination: GainNode,
    frequency: number,
    durationMs: number,
    startTime: number,
    velocity: number,
  ) {
    const vel = velocity * volume / 0.35; // normalize to volume

    // Pure sine - warm body
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(frequency, startTime);

    // Gentle chorus - width without harshness
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(frequency * 1.001, startTime);

    // 3rd harmonic - crystalline brightness
    const osc3 = ctx.createOscillator();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(frequency * 3, startTime);

    // 5th harmonic - bell character
    const osc4 = ctx.createOscillator();
    osc4.type = 'sine';
    osc4.frequency.setValueAtTime(frequency * 5, startTime);

    const chorusGain = ctx.createGain();
    chorusGain.gain.setValueAtTime(0.15, startTime);

    const harmonic3Gain = ctx.createGain();
    harmonic3Gain.gain.setValueAtTime(0.12, startTime);

    const harmonic5Gain = ctx.createGain();
    harmonic5Gain.gain.setValueAtTime(0.04, startTime);

    // ADSR envelope - percussive marimba/bell
    const envelope = ctx.createGain();
    const attackEnd = startTime + ATTACK_MS / 1000;
    const decayEnd = attackEnd + DECAY_MS / 1000;
    const sustainEnd = startTime + durationMs / 1000;
    const releaseEnd = sustainEnd + RELEASE_MS / 1000;

    envelope.gain.setValueAtTime(0, startTime);
    envelope.gain.linearRampToValueAtTime(vel, attackEnd);
    envelope.gain.linearRampToValueAtTime(vel * 0.65, decayEnd);
    envelope.gain.setValueAtTime(vel * 0.55, sustainEnd);
    envelope.gain.exponentialRampToValueAtTime(0.001, releaseEnd);

    // Routing
    osc1.connect(envelope);
    osc2.connect(chorusGain);
    chorusGain.connect(envelope);
    osc3.connect(harmonic3Gain);
    harmonic3Gain.connect(envelope);
    osc4.connect(harmonic5Gain);
    harmonic5Gain.connect(envelope);
    envelope.connect(destination);

    osc1.start(startTime);
    osc2.start(startTime);
    osc3.start(startTime);
    osc4.start(startTime);
    osc1.stop(releaseEnd + 0.01);
    osc2.stop(releaseEnd + 0.01);
    osc3.stop(releaseEnd + 0.01);
    osc4.stop(releaseEnd + 0.01);
  }

  function stopRingtone() {
    isPlayingRef.current = false;

    if (audioElementRef.current) {
      try {
        audioElementRef.current.pause();
        audioElementRef.current.currentTime = 0;
      } catch { /* ignore */ }
      audioElementRef.current = null;
    }

    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }

    if (masterGainRef.current) {
      const ctx = getSharedAudioContext();
      if (ctx && ctx.state !== 'closed') {
        try {
          const now = ctx.currentTime;
          masterGainRef.current.gain.setValueAtTime(masterGainRef.current.gain.value, now);
          masterGainRef.current.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        } catch { /* ignore */ }
      }
      setTimeout(() => {
        try { masterGainRef.current?.disconnect(); } catch { /* ignore */ }
        masterGainRef.current = null;
      }, 120);
    }
  }
}
